import ISBN from 'isbn3';

// ISBN-Anzeige mit Bindestrichen.
//
// Die Trennstellen einer ISBN sind nicht gleichmäßig: sie hängen von
// Gruppe (Sprachraum) und Verlagsnummer ab. 978-3-423-28278-9 gehört dem
// deutschen Sprachraum (3) und dem Verlag 423; 978-3-00-012345-0 ist eine
// Eigennummer ohne Verlagskennung. Wer das mit einer festen Maske
// nachbaut, setzt die Striche für die meisten Verlage falsch.
//
// Deshalb `isbn3` (MIT): die Bibliothek trägt die offiziellen
// Bereichsdaten der Internationalen ISBN-Agentur und rechnet die
// Trennstellen daraus aus. 208 KB Quelltext, im Bundle wiegt der
// Bereichsdatensatz gut 30 KB gzip — dafür stimmen die Striche.
//
// WICHTIG: Das hier ist ausschließlich Anzeige. Gespeichert, gesucht,
// kopiert und an die VLB-Schnittstelle gegeben werden weiterhin nackte
// Ziffern. Wer den Rückweg braucht, nimmt isbnNackt().

/**
 * Formatiert eine ISBN für die Anzeige.
 *
 * Die Form folgt dem, was gespeichert ist: eine ISBN-10 bleibt eine
 * ISBN-10. `isbn3` könnte jede Zehnstellige in die 13er-Form umrechnen —
 * das wäre aber eine andere Nummer als die auf dem Buch, und beim
 * Vergleichen mit dem Lieferschein stünde man dumm da.
 *
 * Unbekanntes bleibt unverändert stehen. Lieber eine ISBN ohne Striche
 * als eine verschluckte: im Bestand stehen auch Nummern, die kein
 * gültiges Prüfzeichen haben.
 */
export function isbnAnzeige(roh: string | null | undefined): string {
  if (roh === null || roh === undefined) return '';
  if (roh.trim() === '') return '';
  const nackt = isbnNackt(roh);
  // Steht da etwas ohne eine einzige Ziffer, ist es keine ISBN — aber es
  // stand da, und es bleibt stehen. Ein leerer Rueckgabewert haette den
  // Inhalt kommentarlos verschluckt.
  const geparst = nackt === '' ? null : ISBN.parse(nackt);
  if (geparst === null) return roh;
  const mitStrichen = nackt.length === 10 ? geparst.isbn10h : geparst.isbn13h;
  return mitStrichen ?? roh;
}

/** Nackte Ziffern — für Suche, Speicherung, Zwischenablage. */
export function isbnNackt(roh: string | null | undefined): string {
  if (roh === null || roh === undefined) return '';
  return roh.replace(/[^0-9Xx]/g, '').toUpperCase();
}

/**
 * Ist das eine vollstaendige, gueltige ISBN?
 *
 * Nicht die Zeichenzahl entscheidet, sondern die Pruefziffer. "9783772528911"
 * hat dreizehn Ziffern und ist trotzdem keine ISBN; "043942089X" hat zehn
 * Zeichen, davon eines keine Ziffer, und ist eine.
 *
 * Die Pruefung macht `isbn3`, nicht wir: `ISBN.parse()` liefert null,
 * sobald Struktur oder Pruefziffer nicht stimmen. Eine zweite, eigene
 * Rechnung daneben waere eine zweite Wahrheit ueber dieselbe Frage
 * (Designprinzip 4) — und die Bereichsdaten der ISBN-Agentur haben wir
 * ohnehin nur dort.
 *
 * Striche und Leerzeichen zaehlen nicht mit: geprueft wird die nackte
 * Nummer, wie ueberall sonst auch.
 */
export function isbnGueltig(roh: string | null | undefined): boolean {
  const nackt = isbnNackt(roh);
  if (nackt === '') return false;
  const geparst = ISBN.parse(nackt);
  return geparst !== null && geparst.isValid === true;
}

/**
 * Warum eine getippte ISBN nicht genommen wird — als Satz für die
 * Oberfläche, oder `null`, wenn sie gültig ist (E62).
 *
 * Erlaubt sind Ziffern, Bindestriche, Leerzeichen und ein X am Ende.
 * Andere Zeichen würde isbnNackt() still verschlucken; aus „978-3-a00…"
 * würde dann eine Nummer, die niemand getippt hat. Deshalb werden sie
 * hier ausdrücklich abgewiesen, bevor überhaupt gerechnet wird.
 *
 * Ob die Nummer gilt, entscheidet isbnGueltig() — derselbe Maßstab wie
 * überall sonst. Der Satz unterscheidet nur, WAS nicht passt, damit am
 * Tresen klar ist, ob man sich verzählt oder vertippt hat.
 */
export function isbnEingabeFehler(roh: string): string | null {
  if (/[^0-9Xx\s-]/.test(roh)) {
    return 'Nur Ziffern und Bindestriche, bei einer zehnstelligen ISBN am Ende auch ein X.';
  }
  const nackt = isbnNackt(roh);
  if (nackt.length !== 10 && nackt.length !== 13) {
    return `Eine ISBN hat 10 oder 13 Stellen — hier sind es ${nackt.length}.`;
  }
  if (nackt.slice(0, -1).includes('X')) {
    return 'Ein X steht nur ganz am Ende einer zehnstelligen ISBN.';
  }
  if (isbnGueltig(nackt)) return null;
  if (nackt.length === 13 && !nackt.startsWith('978') && !nackt.startsWith('979')) {
    return 'Das ist keine ISBN — eine dreizehnstellige beginnt mit 978 oder 979.';
  }
  // Bewusst nicht „die Prüfziffer passt nicht": isbnGueltig() prüft auch
  // die Bereichsdaten der ISBN-Agentur. Eine Nummer mit richtiger
  // Prüfziffer in einem nie vergebenen Bereich scheitert ebenfalls — der
  // Satz muss für beide Fälle stimmen.
  return 'Keine gültige ISBN — meist ist eine Ziffer vertippt oder vertauscht. Bitte mit der Vorlage vergleichen.';
}
